import type {
  AltYBootstrap,
  AltYBootstrapApiConfig,
  AltYBootstrapAuthConfig,
  AltYBootstrapCapabilities,
  AltYBootstrapConfig,
  AltYBootstrapSource,
  AltYBootstrapUser,
  AltYEnvironmentTab,
  AltYShellConfig,
} from '../types/global';
import { fetchWithAltYNativeAuth, getNativeAuthToken } from './nativeAuth';

const DEFAULT_TABS: AltYEnvironmentTab[] = [
  { id: 'ai', label: 'AI', url: 'https://ai.idsys.ee/', icon: 'claude' },
  { id: 'testing', label: 'Testing', url: 'https://testing.idsys.ee/', icon: 'fa-flask' },
  { id: 'internal', label: 'Internal', url: 'https://internal.idsys.ee/', icon: 'fa-building' },
  { id: 'online', label: 'Online', url: 'https://online.idsys.ee/', icon: 'fa-globe' },
];

const DEFAULT_API: AltYBootstrapApiConfig = {
  baseUrl: '/api/alty',
  wsBaseUrl: '/ws',
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function boolOrNull(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null;
}

function stringOrUndefined(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function normalizeBase(value: unknown, fallback: string): string {
  const raw = stringOrUndefined(value) || fallback;
  return raw.replace(/\/+$/, '') || fallback;
}

function normalizeOptionalBase(value: unknown): string | undefined {
  const raw = stringOrUndefined(value);
  return raw ? raw.replace(/\/+$/, '') || undefined : undefined;
}

function readViteEnv(): Record<string, unknown> {
  const env = (import.meta as unknown as { env?: unknown }).env;
  return isRecord(env) ? env : {};
}

function wsOriginFromAppBase(appBaseUrl: string): string {
  if (appBaseUrl.startsWith('https://')) return `wss://${appBaseUrl.slice('https://'.length)}`;
  if (appBaseUrl.startsWith('http://')) return `ws://${appBaseUrl.slice('http://'.length)}`;
  return appBaseUrl;
}

function publicUrlFromApiBase(apiBaseUrl: string): URL | null {
  try {
    return new URL(apiBaseUrl);
  } catch {
    return null;
  }
}

function isLocalHostname(hostname: string): boolean {
  return hostname === 'localhost'
    || hostname === '127.0.0.1'
    || hostname === '0.0.0.0'
    || hostname === '::1'
    || hostname === '[::1]';
}

function brokerUrlFromApiBase(apiBaseUrl: string): string | undefined {
  const parsed = publicUrlFromApiBase(apiBaseUrl);
  return parsed ? parsed.origin : undefined;
}

function wsBaseFromApiBase(apiBaseUrl: string): string | undefined {
  const parsed = publicUrlFromApiBase(apiBaseUrl);
  if (!parsed) return undefined;
  const basePath = parsed.pathname.replace(/\/api\/alty\/?$/, '').replace(/\/+$/, '');
  return `${wsOriginFromAppBase(parsed.origin)}${basePath}/ws`;
}

function defaultWsBaseUrl(apiBaseUrl: string, brokerUrl?: string): string {
  const apiWsBaseUrl = wsBaseFromApiBase(apiBaseUrl);
  if (!brokerUrl) return apiWsBaseUrl || DEFAULT_API.wsBaseUrl;

  const api = publicUrlFromApiBase(apiBaseUrl);
  const broker = publicUrlFromApiBase(brokerUrl);
  const brokerPath = broker?.pathname.replace(/\/+$/, '') || '';
  if (apiWsBaseUrl && api && broker && api.origin === broker.origin && !brokerPath) {
    return apiWsBaseUrl;
  }

  return `${wsOriginFromAppBase(brokerUrl)}/ws`;
}

function apiConfigFromViteEnv(): Partial<AltYBootstrapApiConfig> | null {
  const env = readViteEnv();
  const hasRuntimeEnv = [
    'VITE_ALTY_APP_BASE_URL',
    'VITE_ALTY_API_BASE_URL',
    'VITE_ALTY_BOOTSTRAP_URL',
    'VITE_ALTY_MINT_URL',
    'VITE_ALTY_BROKER_URL',
    'VITE_ALTY_WS_BASE_URL',
    'VITE_ALTY_AUTH_ME_URL',
    'VITE_ALTY_LOGIN_REQUEST_URL',
    'VITE_ALTY_LOGIN_VERIFY_URL',
    'VITE_ALTY_LOGOUT_URL',
  ].some((key) => !!stringOrUndefined(env[key]));

  if (!hasRuntimeEnv) return null;

  const appBaseUrl = normalizeBase(env.VITE_ALTY_APP_BASE_URL, '');
  const baseUrl = normalizeBase(
    env.VITE_ALTY_API_BASE_URL,
    appBaseUrl ? `${appBaseUrl}/api/alty` : DEFAULT_API.baseUrl,
  );
  const brokerUrl = normalizeOptionalBase(env.VITE_ALTY_BROKER_URL)
    || appBaseUrl
    || brokerUrlFromApiBase(baseUrl);

  return {
    baseUrl,
    bootstrapUrl: normalizeOptionalBase(env.VITE_ALTY_BOOTSTRAP_URL) || `${baseUrl}/bootstrap`,
    mintUrl: normalizeOptionalBase(env.VITE_ALTY_MINT_URL) || `${baseUrl}/mint`,
    brokerUrl,
    wsBaseUrl: normalizeBase(env.VITE_ALTY_WS_BASE_URL, defaultWsBaseUrl(baseUrl, brokerUrl)),
    authMeUrl: normalizeOptionalBase(env.VITE_ALTY_AUTH_ME_URL) || `${baseUrl}/auth/me`,
    loginRequestUrl: normalizeOptionalBase(env.VITE_ALTY_LOGIN_REQUEST_URL) || `${baseUrl}/auth/request-code`,
    loginVerifyUrl: normalizeOptionalBase(env.VITE_ALTY_LOGIN_VERIFY_URL) || `${baseUrl}/auth/verify-code`,
    logoutUrl: normalizeOptionalBase(env.VITE_ALTY_LOGOUT_URL) || `${baseUrl}/auth/logout`,
  };
}

function normalizeApiConfig(value: unknown): AltYBootstrapApiConfig {
  const source = isRecord(value) ? value : {};
  const baseUrl = normalizeBase(source.baseUrl, DEFAULT_API.baseUrl);
  const derivedBrokerUrl = brokerUrlFromApiBase(baseUrl);
  const brokerUrl = normalizeOptionalBase(source.brokerUrl) || derivedBrokerUrl;
  const wsBaseUrl = normalizeBase(source.wsBaseUrl, defaultWsBaseUrl(baseUrl, brokerUrl));
  return {
    baseUrl,
    wsBaseUrl,
    bootstrapUrl: normalizeOptionalBase(source.bootstrapUrl) || `${baseUrl}/bootstrap`,
    mintUrl: normalizeOptionalBase(source.mintUrl) || `${baseUrl}/mint`,
    brokerUrl,
    authMeUrl: normalizeOptionalBase(source.authMeUrl) || `${baseUrl}/auth/me`,
    loginRequestUrl: normalizeOptionalBase(source.loginRequestUrl) || `${baseUrl}/auth/request-code`,
    loginVerifyUrl: normalizeOptionalBase(source.loginVerifyUrl) || `${baseUrl}/auth/verify-code`,
    logoutUrl: normalizeOptionalBase(source.logoutUrl) || `${baseUrl}/auth/logout`,
  };
}

function apiConfigFromWindow(): AltYBootstrapApiConfig {
  const resolvedApi = window.altYResolvedBootstrap?.source === 'dev-fallback'
    ? undefined
    : window.altYResolvedBootstrap?.api;
  return normalizeApiConfig(
    window.altYBootstrapConfig?.api
    || window.altYStandaloneConfig?.api
    || apiConfigFromViteEnv()
    || resolvedApi,
  );
}

function apiBaseFromWindow(): string {
  return apiConfigFromWindow().baseUrl;
}

function bootstrapUrlFromWindow(): string {
  return apiConfigFromWindow().bootstrapUrl || `${apiBaseFromWindow()}/bootstrap`;
}

function normalizeTabs(value: unknown): AltYEnvironmentTab[] {
  const rawTabs = Array.isArray(value) ? value : [];
  const tabs: AltYEnvironmentTab[] = [];
  for (const tab of rawTabs) {
    if (!isRecord(tab)) continue;
    const id = stringOrUndefined(tab.id);
    const label = stringOrUndefined(tab.label);
    const url = stringOrUndefined(tab.url);
    if (!id || !label || !url) continue;
    const matchedPreset = DEFAULT_TABS.find((preset) => preset.id === id);
    tabs.push({
      id,
      label,
      url,
      icon: stringOrUndefined(tab.icon) || matchedPreset?.icon || 'fa-globe',
    });
  }
  return tabs.length > 0 ? tabs : DEFAULT_TABS;
}

export function normalizeAltYShellConfig(value: unknown): AltYShellConfig {
  const source = isRecord(value) ? value : {};
  const tabs = normalizeTabs(source.tabs);
  return {
    standalone: boolOrNull(source.standalone) ?? true,
    initialUrl: stringOrUndefined(source.initialUrl) || tabs[0]?.url || '/',
    tabs,
  };
}

function normalizeUser(value: unknown): AltYBootstrapUser | undefined {
  if (!isRecord(value)) return undefined;
  const email = stringOrUndefined(value.email) || stringOrUndefined(value.userName);
  const name = stringOrUndefined(value.name) || stringOrUndefined(value.displayName) || stringOrUndefined(value.label);
  const id = stringOrUndefined(value.id) || stringOrUndefined(value.userId);
  const isSuperAdmin = boolOrNull(value.isSuperAdmin);
  const user: AltYBootstrapUser = {};
  if (id) user.id = id;
  if (email) user.email = email;
  if (name) {
    user.name = name;
    user.displayName = name;
  }
  if (isSuperAdmin !== null) user.isSuperAdmin = isSuperAdmin;
  return Object.keys(user).length > 0 ? user : undefined;
}

function normalizeCapabilities(
  value: unknown,
  user: AltYBootstrapUser | undefined,
  authenticated: boolean | null,
): AltYBootstrapCapabilities {
  const source = isRecord(value) ? value : {};
  const isSuperAdmin = boolOrNull(source.isSuperAdmin) ?? user?.isSuperAdmin ?? false;
  const explicitConfigAccess = boolOrNull(source.canAccessAllConfigPages);
  const canAccessAllConfigPages = isSuperAdmin || (explicitConfigAccess ?? (authenticated === true));
  return {
    isSuperAdmin,
    canAccessAllConfigPages,
    canvasMode: boolOrNull(source.canvasMode) ?? true,
    canvasOnlyMode: boolOrNull(source.canvasOnlyMode) ?? true,
  };
}

function defaultAuthMethod(
  source: AltYBootstrapSource,
  authenticated: boolean,
): AltYBootstrapAuthConfig['method'] {
  if (source === 'globals') return 'razor-globals';
  if (source === 'standalone') return authenticated ? 'native' : 'none';
  if (source === 'dev-fallback') return 'dev-fallback';
  if (source === 'api') return authenticated ? 'cookie' : 'none';
  return undefined;
}

function normalizeAuthConfig(
  source: AltYBootstrapSource,
  value: unknown,
  authenticated: boolean,
  api: AltYBootstrapApiConfig,
): AltYBootstrapAuthConfig {
  const config = isRecord(value) ? value : {};
  const rawState = stringOrUndefined(config.state);
  const state: AltYBootstrapAuthConfig['state'] = rawState === 'authenticated' || rawState === 'anonymous' || rawState === 'unknown'
    ? rawState
    : authenticated
      ? 'authenticated'
      : 'anonymous';
  const rawMethod = stringOrUndefined(config.method);
  const method: AltYBootstrapAuthConfig['method'] =
    rawMethod === 'razor-globals' || rawMethod === 'cookie' || rawMethod === 'native' || rawMethod === 'none'
      || rawMethod === 'dev-smoke' || rawMethod === 'dev-fallback'
      ? rawMethod
      : defaultAuthMethod(source, authenticated);
  return {
    state,
    method,
    loginRequestUrl: stringOrUndefined(config.loginRequestUrl) || api.loginRequestUrl,
    loginVerifyUrl: stringOrUndefined(config.loginVerifyUrl) || api.loginVerifyUrl,
    logoutUrl: stringOrUndefined(config.logoutUrl) || api.logoutUrl,
  };
}

function authMethodFromAuthMe(
  value: Record<string, unknown>,
  hasNativeAuthToken: boolean,
): AltYBootstrapAuthConfig['method'] {
  const auth = isRecord(value.auth) ? value.auth : {};
  const rawMethod = stringOrUndefined(auth.method) || stringOrUndefined(value.authMethod);
  if (rawMethod === 'native' || rawMethod === 'cookie') return rawMethod;
  return hasNativeAuthToken ? 'native' : 'cookie';
}

function userFromAuthMe(value: Record<string, unknown>): AltYBootstrapUser | undefined {
  const auth = isRecord(value.auth) ? value.auth : {};
  return normalizeUser(value) || normalizeUser(auth.user);
}

function authMeIsAnonymous(value: Record<string, unknown>): boolean {
  const auth = isRecord(value.auth) ? value.auth : {};
  const authenticated = boolOrNull(value.authenticated) ?? boolOrNull(auth.authenticated);
  const state = stringOrUndefined(auth.state);
  return authenticated === false || state === 'anonymous';
}

function finalizeBootstrap(
  source: AltYBootstrapSource,
  config: AltYBootstrapConfig = {},
): AltYBootstrap {
  const shell = normalizeAltYShellConfig(config.shell);
  const user = normalizeUser(config.user);
  const explicitAuth = boolOrNull(config.authenticated);
  const authenticated = explicitAuth ?? !!user?.email;
  const capabilities = normalizeCapabilities(config.capabilities, user, authenticated);
  const api = normalizeApiConfig(config.api);
  const bootstrap: AltYBootstrap = {
    schemaVersion: 1,
    source,
    authenticated: authenticated || capabilities.isSuperAdmin || capabilities.canAccessAllConfigPages,
    shell,
    capabilities,
    api,
    auth: normalizeAuthConfig(
      source,
      config.auth,
      authenticated || capabilities.isSuperAdmin || capabilities.canAccessAllConfigPages,
      api,
    ),
  };
  if (user) bootstrap.user = user;
  return bootstrap;
}

function applyBootstrapSideEffects(bootstrap: AltYBootstrap): AltYBootstrap {
  window.altYResolvedBootstrap = bootstrap;
  if (!window.altYShellConfig) window.altYShellConfig = bootstrap.shell;
  if (typeof window.isSuperAdmin !== 'boolean') {
    window.isSuperAdmin = bootstrap.capabilities.isSuperAdmin;
  }
  if (typeof window.canAccessAllConfigPages !== 'boolean') {
    window.canAccessAllConfigPages = bootstrap.capabilities.canAccessAllConfigPages;
  }
  return bootstrap;
}

function readGlobalsBootstrap(): AltYBootstrap | null {
  if (window.altYResolvedBootstrap && window.altYResolvedBootstrap.source !== 'globals') {
    return null;
  }
  const hasGlobals = !!window.altYShellConfig
    || typeof window.isSuperAdmin === 'boolean'
    || typeof window.canAccessAllConfigPages === 'boolean';
  if (!hasGlobals) return null;
  const authenticated = !!(window.isSuperAdmin || window.canAccessAllConfigPages);
  return finalizeBootstrap('globals', {
    authenticated,
    shell: window.altYShellConfig,
    capabilities: {
      isSuperAdmin: !!window.isSuperAdmin,
      canAccessAllConfigPages: !!window.canAccessAllConfigPages,
    },
    api: window.altYBootstrapConfig?.api || window.altYStandaloneConfig?.api,
    auth: window.altYBootstrapConfig?.auth || window.altYStandaloneConfig?.auth,
  });
}

function shouldRequireStrictStandaloneShell(config: AltYBootstrapConfig): boolean {
  const api = normalizeApiConfig(config.api);
  const endpointCandidates = [api.bootstrapUrl, api.baseUrl].filter(Boolean);
  for (const candidate of endpointCandidates) {
    try {
      const parsed = new URL(candidate || '', window.location.href);
      if (!isLocalHostname(parsed.hostname)) return true;
    } catch {
      return !isLocalDevHost();
    }
  }

  try {
    const current = new URL(window.location.href);
    if (current.protocol !== 'http:' && current.protocol !== 'https:') return true;
    return !isLocalHostname(current.hostname);
  } catch {
    return !isLocalDevHost();
  }
}

function readStandaloneBootstrap(): AltYBootstrap | null {
  const config = window.altYBootstrapConfig || window.altYStandaloneConfig;
  if (!config) return null;
  const hasRuntimeState = config.authenticated !== undefined
    || !!config.user
    || !!config.capabilities
    || !!config.shell;
  if (!hasRuntimeState) return null;
  if (shouldRequireStrictStandaloneShell(config)) {
    if (!hasStrictShellConfig({ shell: config.shell })) return null;
    if (!hasStrictStandaloneApiConfig(config)) return null;
  }
  return finalizeBootstrap('standalone', config);
}

function isLocalDevHost(): boolean {
  return isLocalHostname(window.location.hostname);
}

function devFallbackBootstrap(): AltYBootstrap | null {
  if (!isLocalDevHost()) return null;
  if (window.altYBootstrapConfig?.api || window.altYStandaloneConfig?.api || apiConfigFromViteEnv()) return null;
  return finalizeBootstrap('dev-fallback', {
    authenticated: true,
    shell: { standalone: true, initialUrl: '/', tabs: [{ id: 'local', label: 'Local', url: '/' }] },
    capabilities: { isSuperAdmin: true, canAccessAllConfigPages: true },
    api: DEFAULT_API,
    user: { email: 'dev@local', name: 'Local dev', isSuperAdmin: true },
  });
}

function noBootstrap(): AltYBootstrap {
  const api = apiConfigFromWindow();
  const bootstrapUrl = api.bootstrapUrl || `${api.baseUrl}/bootstrap`;
  const strictApiRequired = shouldRequireStrictApiBootstrap(bootstrapUrl);
  const allowCleartext = allowsStrictCleartextBootstrap(bootstrapUrl);
  return finalizeBootstrap('none', {
    authenticated: false,
    capabilities: { isSuperAdmin: false, canAccessAllConfigPages: false },
    shell: {
      standalone: true,
      initialUrl: '/stand-in',
      tabs: [{ id: 'next', label: 'Next', url: '/stand-in', icon: 'fa-flask' }],
    },
    api: strictApiRequired && !hasStrictResolvedApiConfig(api, false, allowCleartext)
      ? DEFAULT_API
      : api,
  });
}

function configFromApiPayload(payload: unknown): AltYBootstrapConfig {
  if (!isRecord(payload)) return {};
  const authRecord = isRecord(payload.auth) ? payload.auth : {};
  const user = normalizeUser(payload.user || authRecord.user);
  const authenticated = boolOrNull(payload.authenticated) ?? boolOrNull(authRecord.authenticated);
  return {
    schemaVersion: typeof payload.schemaVersion === 'number' ? payload.schemaVersion : undefined,
    authenticated: authenticated ?? undefined,
    auth: isRecord(payload.auth) ? payload.auth as Partial<AltYBootstrapAuthConfig> : undefined,
    user,
    capabilities: isRecord(payload.capabilities)
      ? payload.capabilities as Partial<AltYBootstrapCapabilities>
      : undefined,
    shell: isRecord(payload.shell)
      ? payload.shell as Partial<AltYShellConfig>
      : isRecord(payload.config)
        ? payload.config as Partial<AltYShellConfig>
        : undefined,
    api: isRecord(payload.api) ? payload.api as Partial<AltYBootstrapApiConfig> : apiConfigFromWindow(),
  };
}

function shouldRequireStrictApiBootstrap(bootstrapUrl: string): boolean {
  try {
    const parsed = new URL(bootstrapUrl, window.location.href);
    return !isLocalHostname(parsed.hostname);
  } catch {
    return !isLocalDevHost();
  }
}

function canFetchBootstrapUrl(bootstrapUrl: string): boolean {
  try {
    const requested = new URL(bootstrapUrl, window.location.href);
    if (requested.protocol === 'https:' || isLocalHostname(requested.hostname)) return true;
    return requested.protocol === 'http:' && allowsStrictCleartextBootstrap(bootstrapUrl);
  } catch {
    return !shouldRequireStrictApiBootstrap(bootstrapUrl);
  }
}

function allowsStrictCleartextBootstrap(bootstrapUrl: string): boolean {
  try {
    const requested = new URL(bootstrapUrl, window.location.href);
    const current = new URL(window.location.href);
    return requested.protocol === 'http:'
      && current.protocol === 'http:'
      && requested.hostname === current.hostname
      && requested.port === current.port;
  } catch {
    return false;
  }
}

function hasConfiguredApi(config: AltYBootstrapConfig): boolean {
  return isRecord(config.api) && Object.values(config.api).some((value) => !!stringOrUndefined(value));
}

function hasStrictStandaloneApiConfig(config: AltYBootstrapConfig): boolean {
  if (!hasConfiguredApi(config)) return true;
  const api = normalizeApiConfig(config.api);
  const bootstrapUrl = api.bootstrapUrl || `${api.baseUrl}/bootstrap`;
  if (!shouldRequireStrictApiBootstrap(bootstrapUrl)) return true;
  return hasStrictResolvedApiConfig(api, false, allowsStrictCleartextBootstrap(bootstrapUrl));
}

function parseAbsoluteEndpointUrl(
  value: unknown,
  protocols: readonly string[],
  allowLocalHost: boolean,
): URL | null {
  const raw = stringOrUndefined(value);
  if (!raw) return null;
  try {
    const parsed = new URL(raw);
    if (!protocols.includes(parsed.protocol)
      || !parsed.hostname
      || (!allowLocalHost && isLocalHostname(parsed.hostname))) {
      return null;
    }
    if (parsed.username || parsed.password || parsed.search || parsed.hash) return null;
    return parsed;
  } catch {
    return null;
  }
}

function isAbsoluteEndpointUrl(
  value: unknown,
  protocols: readonly string[],
  allowLocalHost: boolean,
): boolean {
  return !!parseAbsoluteEndpointUrl(value, protocols, allowLocalHost);
}

function isOriginOnlyHttpUrl(value: unknown, allowLocalHost: boolean): boolean {
  const parsed = parseAbsoluteEndpointUrl(value, ['http:', 'https:'], allowLocalHost);
  return !!parsed && (parsed.pathname === '' || parsed.pathname === '/');
}

function hasStrictEndpointTopology(
  api: Record<string, unknown>,
  allowLocalHost: boolean,
  allowCleartext: boolean,
): boolean {
  const allowCleartextEndpoints = allowLocalHost || allowCleartext;
  const httpProtocols = allowCleartextEndpoints ? ['http:', 'https:'] : ['https:'];
  const wsProtocols = allowCleartextEndpoints ? ['ws:', 'wss:'] : ['wss:'];
  const base = parseAbsoluteEndpointUrl(api.baseUrl, httpProtocols, allowLocalHost);
  const broker = parseAbsoluteEndpointUrl(api.brokerUrl, httpProtocols, allowLocalHost);
  const ws = parseAbsoluteEndpointUrl(api.wsBaseUrl, wsProtocols, allowLocalHost);
  if (!base || !broker || !ws) return false;
  if (broker.pathname !== '' && broker.pathname !== '/') return false;

  const apiPath = base.pathname.replace(/\/+$/, '');
  const apiSuffix = '/api/alty';
  if (!apiPath.endsWith(apiSuffix)) return false;
  const publicPathPrefix = apiPath.slice(0, -apiSuffix.length);
  const expectedWsProtocol = broker.protocol === 'https:' ? 'wss:' : 'ws:';
  const expectedWsPath = broker.origin === base.origin ? `${publicPathPrefix}/ws` : '/ws';
  if (
    ws.protocol !== expectedWsProtocol
    || ws.host !== broker.host
    || ws.pathname !== expectedWsPath
  ) return false;

  const exactApiEndpointPaths = {
    bootstrapUrl: `${apiPath}/bootstrap`,
    mintUrl: `${apiPath}/mint`,
    authMeUrl: `${apiPath}/auth/me`,
    loginRequestUrl: `${apiPath}/auth/request-code`,
    loginVerifyUrl: `${apiPath}/auth/verify-code`,
    logoutUrl: `${apiPath}/auth/logout`,
  } as const;

  return Object.entries(exactApiEndpointPaths).every(([key, expectedPath]) => {
    const endpoint = parseAbsoluteEndpointUrl(api[key], httpProtocols, allowLocalHost);
    return !!endpoint && endpoint.origin === base.origin && endpoint.pathname === expectedPath;
  });
}

function hasStrictResolvedApiConfig(
  api: AltYBootstrapApiConfig,
  allowLocalHost: boolean,
  allowCleartext: boolean,
): boolean {
  const allowCleartextEndpoints = allowLocalHost || allowCleartext;
  const httpProtocols = allowCleartextEndpoints ? ['http:', 'https:'] : ['https:'];
  const wsProtocols = allowCleartextEndpoints ? ['ws:', 'wss:'] : ['wss:'];
  const httpApiKeys = [
    'baseUrl',
    'bootstrapUrl',
    'mintUrl',
    'brokerUrl',
    'authMeUrl',
    'loginRequestUrl',
    'loginVerifyUrl',
    'logoutUrl',
  ] as const;

  return httpApiKeys.every((key) => isAbsoluteEndpointUrl(api[key], httpProtocols, allowLocalHost))
    && isOriginOnlyHttpUrl(api.brokerUrl, allowLocalHost)
    && isAbsoluteEndpointUrl(api.wsBaseUrl, wsProtocols, allowLocalHost)
    && hasStrictEndpointTopology(api as unknown as Record<string, unknown>, allowLocalHost, allowCleartext);
}

function resolveApiConfigAgainstWindow(api: AltYBootstrapApiConfig): AltYBootstrapApiConfig | null {
  const resolveHttpUrl = (value: string | undefined): string | undefined => (
    value ? new URL(value, window.location.href).href : undefined
  );
  const resolveWsUrl = (value: string): string => {
    const parsed = new URL(value, window.location.href);
    if (parsed.protocol === 'https:') parsed.protocol = 'wss:';
    if (parsed.protocol === 'http:') parsed.protocol = 'ws:';
    return parsed.href;
  };

  try {
    const absoluteBaseUrl = resolveHttpUrl(api.baseUrl) || api.baseUrl;
    return {
      baseUrl: absoluteBaseUrl,
      bootstrapUrl: resolveHttpUrl(api.bootstrapUrl),
      mintUrl: resolveHttpUrl(api.mintUrl),
      brokerUrl: resolveHttpUrl(api.brokerUrl) || new URL(absoluteBaseUrl).origin,
      wsBaseUrl: resolveWsUrl(api.wsBaseUrl),
      authMeUrl: resolveHttpUrl(api.authMeUrl),
      loginRequestUrl: resolveHttpUrl(api.loginRequestUrl),
      loginVerifyUrl: resolveHttpUrl(api.loginVerifyUrl),
      logoutUrl: resolveHttpUrl(api.logoutUrl),
    };
  } catch {
    return null;
  }
}

function hasStrictAuthConfig(payload: Record<string, unknown>): boolean {
  const auth = isRecord(payload.auth) ? payload.auth : null;
  const authenticated = boolOrNull(payload.authenticated);
  const state = auth ? stringOrUndefined(auth.state) : undefined;
  const method = auth ? stringOrUndefined(auth.method) : undefined;
  if (!auth || authenticated === null) return false;
  if (state !== 'authenticated' && state !== 'anonymous') return false;
  if (method !== 'cookie' && method !== 'native' && method !== 'none') return false;
  if (authenticated && state !== 'authenticated') return false;
  if (!authenticated && state !== 'anonymous') return false;
  return state === 'authenticated' ? method === 'cookie' || method === 'native' : method === 'none';
}

function hasStrictCapabilitiesConfig(payload: Record<string, unknown>): boolean {
  const capabilities = isRecord(payload.capabilities) ? payload.capabilities : null;
  const user = isRecord(payload.user) ? payload.user : null;
  const authenticated = boolOrNull(payload.authenticated);
  if (!capabilities || authenticated === null) return false;

  const isSuperAdmin = boolOrNull(capabilities.isSuperAdmin);
  const canAccessAllConfigPages = boolOrNull(capabilities.canAccessAllConfigPages);
  if (isSuperAdmin === null || canAccessAllConfigPages === null) return false;
  const userIsSuperAdmin = user ? boolOrNull(user.isSuperAdmin) : null;
  if (userIsSuperAdmin !== null && userIsSuperAdmin !== isSuperAdmin) return false;
  if (!authenticated && (isSuperAdmin || canAccessAllConfigPages)) return false;
  return !isSuperAdmin || canAccessAllConfigPages;
}

function hasStrictUserIdentityConfig(payload: Record<string, unknown>): boolean {
  const authenticated = boolOrNull(payload.authenticated);
  if (authenticated === null) return false;
  if (!authenticated) return !isRecord(payload.user);

  const user = isRecord(payload.user) ? payload.user : null;
  if (!user) return false;
  return !!(
    stringOrUndefined(user.id)
    || stringOrUndefined(user.email)
    || stringOrUndefined(user.userName)
    || stringOrUndefined(user.name)
    || stringOrUndefined(user.displayName)
  );
}

function isStrictShellRoute(value: unknown): boolean {
  const route = stringOrUndefined(value);
  if (!route || !route.startsWith('/') || route.startsWith('//') || route.includes('\\')) return false;
  try {
    const parsed = new URL(route, 'https://native-shell.invalid');
    return parsed.origin === 'https://native-shell.invalid'
      && `${parsed.pathname}${parsed.search}${parsed.hash}` === route;
  } catch {
    return false;
  }
}

function hasStrictShellConfig(payload: Record<string, unknown>): boolean {
  const shell = isRecord(payload.shell) ? payload.shell : null;
  if (!shell || boolOrNull(shell.standalone) !== true) return false;
  const initialUrl = stringOrUndefined(shell.initialUrl);
  const tabs = Array.isArray(shell.tabs) ? shell.tabs : [];
  if (!isStrictShellRoute(initialUrl) || tabs.length === 0) return false;
  return tabs.every((tab) => {
    if (!isRecord(tab)) return false;
    const tabUrl = stringOrUndefined(tab.url);
    return !!stringOrUndefined(tab.id)
      && !!stringOrUndefined(tab.label)
      && isStrictShellRoute(tabUrl);
  }) && tabs.some((tab) => isRecord(tab) && stringOrUndefined(tab.url) === initialUrl);
}

function hasStrictApiUrls(
  payload: Record<string, unknown>,
  allowLocalHost: boolean,
  allowCleartext: boolean,
): boolean {
  const api = isRecord(payload.api) ? payload.api : null;
  const auth = isRecord(payload.auth) ? payload.auth : null;
  if (!api || !auth) return false;
  const allowCleartextEndpoints = allowLocalHost || allowCleartext;
  const httpProtocols = allowCleartextEndpoints ? ['http:', 'https:'] : ['https:'];
  const wsProtocols = allowCleartextEndpoints ? ['ws:', 'wss:'] : ['wss:'];

  const httpApiKeys = [
    'baseUrl',
    'bootstrapUrl',
    'mintUrl',
    'brokerUrl',
    'authMeUrl',
    'loginRequestUrl',
    'loginVerifyUrl',
    'logoutUrl',
  ] as const;
  const httpAuthKeys = ['loginRequestUrl', 'loginVerifyUrl', 'logoutUrl'] as const;

  return httpApiKeys.every((key) => isAbsoluteEndpointUrl(api[key], httpProtocols, allowLocalHost))
    && isOriginOnlyHttpUrl(api.brokerUrl, allowLocalHost)
    && isAbsoluteEndpointUrl(api.wsBaseUrl, wsProtocols, allowLocalHost)
    && hasStrictEndpointTopology(api, allowLocalHost, allowCleartext)
    && hasStrictAuthConfig(payload)
    && hasStrictCapabilitiesConfig(payload)
    && hasStrictUserIdentityConfig(payload)
    && hasStrictShellConfig(payload)
    && httpAuthKeys.every((key) => isAbsoluteEndpointUrl(auth[key], httpProtocols, allowLocalHost))
    && httpAuthKeys.every((key) => auth[key] === api[key]);
}

function strictBootstrapUrlMatchesRequest(payload: Record<string, unknown>, bootstrapUrl: string): boolean {
  const api = isRecord(payload.api) ? payload.api : null;
  if (!api) return false;

  const advertised = parseAbsoluteEndpointUrl(api.bootstrapUrl, ['http:', 'https:'], false);
  if (!advertised) return false;

  try {
    const requested = new URL(bootstrapUrl, window.location.href);
    if (requested.username || requested.password || requested.search || requested.hash) return false;
    return requested.href === advertised.href;
  } catch {
    return false;
  }
}

function acceptsApiBootstrapPayload(payload: unknown, bootstrapUrl: string): payload is Record<string, unknown> {
  if (!isRecord(payload)) return false;
  if (!shouldRequireStrictApiBootstrap(bootstrapUrl)) return true;
  const allowCleartext = allowsStrictCleartextBootstrap(bootstrapUrl);
  return payload.schemaVersion === 1
    && hasStrictApiUrls(payload, false, allowCleartext)
    && strictBootstrapUrlMatchesRequest(payload, bootstrapUrl);
}

async function fetchJsonUrl(url: string): Promise<unknown | null> {
  try {
    const response = await fetchWithAltYNativeAuth(
      url,
      {
        credentials: 'include',
        cache: 'no-store',
      },
      { api: apiConfigFromWindow() },
    );
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
}

async function fetchApiBootstrap(): Promise<AltYBootstrap | null> {
  const bootstrapUrl = bootstrapUrlFromWindow();
  if (!canFetchBootstrapUrl(bootstrapUrl)) return null;

  const resolvedApi = apiConfigFromWindow();
  const strictApiRequired = shouldRequireStrictApiBootstrap(bootstrapUrl);
  const allowCleartext = allowsStrictCleartextBootstrap(bootstrapUrl);
  if (strictApiRequired) {
    const absoluteApi = resolveApiConfigAgainstWindow(resolvedApi);
    if (!absoluteApi || !hasStrictResolvedApiConfig(absoluteApi, false, allowCleartext)) return null;
  }

  const payload = await fetchJsonUrl(bootstrapUrl);
  if (acceptsApiBootstrapPayload(payload, bootstrapUrl)) {
    const config = configFromApiPayload(payload);
    if (config.authenticated !== undefined || config.user || config.shell || config.capabilities) {
      return finalizeBootstrap('api', config);
    }
  }

  // Remote native/standalone callers must resolve the complete shell and API
  // topology from the versioned bootstrap contract. Falling through to
  // /auth/me after rejecting that payload would synthesize DEFAULT_TABS and
  // accidentally reintroduce the embedded IDsys shell in a native client.
  if (strictApiRequired) return null;

  const authMe = await fetchJsonUrl(resolvedApi.authMeUrl || `${resolvedApi.baseUrl}/auth/me`);
  if (authMe && isRecord(authMe)) {
    const authMeUser = userFromAuthMe(authMe);
    const localDev = isLocalDevHost();
    if (authMeIsAnonymous(authMe) || (!authMeUser && !localDev)) return null;

    const nativeAuthToken = await getNativeAuthToken();
    const isSuperAdmin = boolOrNull(authMe.isSuperAdmin) ?? (localDev ? true : false);
    const canAccessAllConfigPages = boolOrNull(authMe.canAccessAllConfigPages) ?? isSuperAdmin;
    return finalizeBootstrap('api', {
      authenticated: true,
      user: authMeUser || { email: 'dev' },
      capabilities: {
        isSuperAdmin,
        canAccessAllConfigPages,
      },
      auth: { state: 'authenticated', method: authMethodFromAuthMe(authMe, !!nativeAuthToken) },
      api: resolvedApi,
    });
  }

  return null;
}

export function readSynchronousAltYBootstrap(): AltYBootstrap | null {
  const bootstrap = readGlobalsBootstrap()
    || readStandaloneBootstrap()
    || devFallbackBootstrap();
  return bootstrap ? applyBootstrapSideEffects(bootstrap) : null;
}

export async function loadAltYBootstrap(): Promise<AltYBootstrap> {
  const bootstrap = readGlobalsBootstrap()
    || readStandaloneBootstrap()
    || (await fetchApiBootstrap())
    || devFallbackBootstrap()
    || noBootstrap();
  return applyBootstrapSideEffects(bootstrap);
}
